// components/Form2316List.js
import React, { useState, useMemo } from 'react';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/numberUtils';
import form2316API from '../api/form2316API';
import ConfirmModal from './ConfirmModal';
import notify from '../lib/notify';

const Form2316List = ({ records, onRecordDeleted, onRecordUpdated, showActions = true, client }) => {
    const [sortField, setSortField] = useState('employeeName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterExemptionStatus, setFilterExemptionStatus] = useState('all');
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Filter and sort records
    const filteredAndSortedRecords = useMemo(() => {
        let filtered = [...records];

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(record =>
                record.employeeName?.toLowerCase().includes(term) ||
                record.employeeTin?.toLowerCase().includes(term) ||
                record.position?.toLowerCase().includes(term) ||
                record.employeeId?.toLowerCase().includes(term)
            );
        }

        // Apply status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(record => record.status === filterStatus);
        }

        // Apply exemption status filter
        if (filterExemptionStatus !== 'all') {
            filtered = filtered.filter(record => record.employeeExemptionStatus === filterExemptionStatus);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            // Handle numbers
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Handle strings
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return 0;
        });

        return filtered;
    }, [records, searchTerm, filterStatus, filterExemptionStatus, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Workflow status — semantic ramp per DESIGN.md §5.1 (success/warning/danger/neutral)
    const getStatusColor = (status) => {
        switch (status) {
            case 'Draft': return 'bg-ink-canvas text-ink-body';
            case 'Submitted': return 'bg-warning-50 text-warning-700';
            case 'Verified': return 'bg-warning-50 text-warning-700';
            case 'Certified': return 'bg-success-50 text-success-700';
            case 'Cancelled': return 'bg-danger-50 text-danger-700';
            default: return 'bg-ink-canvas text-ink-body';
        }
    };

    const getExemptionStatusLabel = (status) => {
        const options = form2316API.getExemptionStatusOptions();
        const option = options.find(opt => opt.value === status);
        return option ? option.label.split(' - ')[0] : status;
    };

    const handleDelete = (recordId) => {
        setPendingDelete(recordId);
    };

    const confirmDelete = async () => {
        const recordId = pendingDelete;
        if (!recordId) return;

        try {
            setDeleting(true);
            await onRecordDeleted(recordId);
        } catch (error) {
            console.error('Error deleting record:', error);
            notify.error("Couldn't delete", error.message || 'Unknown error — try again.');
        } finally {
            setDeleting(false);
            setPendingDelete(null);
        }
    };

    const handleCertify = async (record) => {
        try {
            const certificateNumber = prompt('Enter certificate number:');
            if (!certificateNumber) return;

            const certificationData = {
                certificateNumber,
                certificateDate: new Date().toISOString().split('T')[0],
                preparedBy: 'Prepared By',
                approvedBy: 'Approved By',
                positionTitle: 'Position Title'
            };

            await form2316API.certify(record._id, certificationData);
            notify.success('Certified', `Record ${certificateNumber} certified.`);
            onRecordUpdated();
        } catch (error) {
            console.error('Error certifying record:', error);
            notify.error("Couldn't certify", error.message || 'Unknown error — try again.');
        }
    };

    if (records.length === 0) {
        return (
            <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-ink-body" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-ink">No Form 2316 records</h3>
                <p className="mt-1 text-sm text-ink-body">Get started by creating a new record.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <div className="relative flex-1 max-w-xs">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-ink-body" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-ink-line rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-full"
                        />
                    </div>
                    
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="border border-ink-line rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value="all">All Status</option>
                        <option value="Draft">Draft</option>
                        <option value="Submitted">Submitted</option>
                        <option value="Verified">Verified</option>
                        <option value="Certified">Certified</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                    
                    <select
                        value={filterExemptionStatus}
                        onChange={(e) => setFilterExemptionStatus(e.target.value)}
                        className="border border-ink-line rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value="all">All Exemptions</option>
                        {form2316API.getExemptionStatusOptions().map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label.split(' - ')[0]}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="text-sm text-ink-body">
                    Showing {filteredAndSortedRecords.length} of {records.length} records
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ink-line">
                    <thead className="bg-ink-canvas">
                        <tr>
                            <th 
                                className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                                onClick={() => handleSort('employeeName')}
                            >
                                <div className="flex items-center">
                                    Employee
                                    {sortField === 'employeeName' && (
                                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                </div>
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">
                                TIN / Status
                            </th>
                            <th 
                                className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                                onClick={() => handleSort('basicSalary')}
                            >
                                <div className="flex items-center">
                                    Basic Salary
                                    {sortField === 'basicSalary' && (
                                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                </div>
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">
                                Gross Compensation
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">
                                Tax Computation
                            </th>
                            {showActions && (
                                <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">
                                    Actions
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-line">
                        {filteredAndSortedRecords.map((record) => {
                            const totalDeductions = (record.sssContributions || 0) + 
                                                   (record.philhealthContributions || 0) + 
                                                   (record.pagibigContributions || 0);
                            const taxRefund = record.taxRefund || 0;
                            const taxPayable = (record.taxDue || 0) - (record.taxWithheld || 0);
                            
                            return (
                                <tr key={record._id} className="hover:bg-ink-canvas">
                                    <td className="px-3 py-2">
                                        <div className="text-sm text-ink">
                                            <div className="font-medium">{record.employeeName}</div>
                                            {record.position && (
                                                <div className="text-xs text-ink-body">{record.position}</div>
                                            )}
                                            {record.employeeId && (
                                                <div className="text-xs text-ink-body">ID: {record.employeeId}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="text-sm text-ink-body">
                                            {record.employeeTin}
                                        </div>
                                        <div className="mt-1">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(record.status)}`}>
                                                {record.status}
                                            </span>
                                        </div>
                                        <div className="text-xs text-ink-body mt-1">
                                            {getExemptionStatusLabel(record.employeeExemptionStatus)}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-ink">
                                        {formatCurrency(record.basicSalary || 0)}
                                        <div className="text-xs text-ink-body mt-1">
                                            Freq: {record.frequencyOfPayment}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-sm">
                                        <div className="text-ink">{formatCurrency(record.grossCompensationIncome || 0)}</div>
                                        <div className="text-xs text-ink-body">
                                            Deductions: {formatCurrency(totalDeductions)}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="space-y-1">
                                            <div className="text-sm">
                                                <span className="text-ink-body">Tax Due: </span>
                                                <span className="font-medium text-ink tabular-nums">{formatCurrency(record.taxDue || 0)}</span>
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-ink-body">Withheld: </span>
                                                <span className="font-medium text-ink tabular-nums">{formatCurrency(record.taxWithheld || 0)}</span>
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-ink-body">Balance: </span>
                                                <span className={`font-medium tabular-nums ${
                                                    taxPayable > 0 ? 'text-warning-700' : 'text-success-600'
                                                }`}>
                                                    {taxPayable > 0
                                                        ? `Payable: ${formatCurrency(taxPayable)}`
                                                        : `Refund: ${formatCurrency(Math.abs(taxPayable))}`
                                                    }
                                                </span>
                                            </div>
                                            {record.certificateNumber && (
                                                <div className="text-xs text-ink-body mt-1">
                                                    Cert: {record.certificateNumber}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {showActions && (
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-ink-body">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    type="button"
                                                    onClick={() => onRecordUpdated(record)}
                                                    className="text-primary-500 hover:text-primary-700"
                                                    title="Edit record"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                {record.status !== 'Certified' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCertify(record)}
                                                        className="text-success-600 hover:text-success-700"
                                                        title="Certify record"
                                                    >
                                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(record._id)}
                                                    className="text-danger-500 hover:text-danger-700"
                                                    title="Delete record"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Summary Footer */}
            {filteredAndSortedRecords.length > 0 && (
                <div className="bg-ink-canvas p-3 rounded border">
                    <div className="flex justify-between items-center">
                        <div className="text-sm text-ink-body">
                            {filteredAndSortedRecords.length} record(s) • {new Set(filteredAndSortedRecords.map(r => r.employeeTin)).size} unique employee(s)
                        </div>
                        <div className="text-sm text-ink font-medium">
                            Total Tax Withheld: {formatCurrency(
                                filteredAndSortedRecords.reduce((sum, record) => sum + (record.taxWithheld || 0), 0)
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={!!pendingDelete}
                title="Delete this record?"
                message="This can't be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="danger"
                loading={deleting}
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default Form2316List;